import { useRouter } from 'next/router';
import { useState } from 'react';

import { TeamUserAvatar } from '@poseidon/components/Avatar';
import { cn } from '@poseidon/components/cn';
import { Button, ButtonLink } from '@poseidon/components/Input2';
import { Editor, General } from '@poseidon/components/StrokeIcons';

import type { AppEnvironment } from '~/utils/appEnvironment';
import { useAuthContext } from '~/auth/auth.context';
import { isSuperadminPath } from '~/auth/superadminPath';
import { SuperadminSwitchPalette, switchShortcutLabel } from '~/components/SuperadminSwitch/SuperadminSwitchPalette';
import { appEnvironmentFromHost } from '~/utils/appEnvironment';

const ENV_BADGE: Record<AppEnvironment, { label: string; className: string }> = {
  local: { label: 'LOCAL', className: 'bg-amber-400 text-amber-950' },
  dev: { label: 'DEV', className: 'bg-sky-400 text-sky-950' },
  staging: { label: 'STAGING', className: 'bg-violet-400 text-violet-950' },
  prod: { label: 'PROD', className: 'bg-rose-500 text-white' },
};

/**
 * The superadmin bar: a slim strip on every page for superadmins. It shows which
 * environment you're in, links to Superadmin, says who you're acting as, and
 * opens the switch palette (⌘⇧K anywhere) to become anyone on any team in one step.
 *
 * On /superadmin pages requests run as you, not the impersonated user (see
 * `utils/api.ts`), so the bar says so there instead of claiming to act as someone.
 */
export const SuperadminBar = () => {
  const router = useRouter();
  const [auth, { stopImpersonation }] = useAuthContext();
  const [paletteOpen, setPaletteOpen] = useState(false);

  const impersonation = auth.impersonation;
  const userTeam = auth.user != null ? auth.userTeam : null;
  const isSuperAdmin = impersonation != null || auth.user?.isSuperAdmin === true;

  if (!isSuperAdmin) return null;

  const env = appEnvironmentFromHost(typeof window === 'undefined' ? '' : window.location.hostname);
  const onSuperadminPage = isSuperadminPath(router.pathname);

  const impersonatedUserTeam =
    impersonation == null
      ? null
      : {
          profilePicture: impersonation.profilePicture ?? null,
          // On superadmin pages the signed-in user is you (requests there run as you), so the
          // impersonated person is named from the session instead.
          userTeamName: (onSuperadminPage ? null : userTeam?.userTeamName) ?? impersonation.email,
          userEmail: impersonation.email,
        };

  return (
    <div
      className="flex h-9 shrink-0 items-center gap-3 border-b border-slate-800 bg-slate-900 px-3 text-xs text-slate-200"
      role="region"
      aria-label="Superadmin bar"
      data-testid="superadmin-bar">
      <span
        className={cn('rounded px-1.5 py-0.5 font-mono text-2xs font-bold tracking-wide', ENV_BADGE[env].className)}
        data-testid="superadmin-bar-env">
        {ENV_BADGE[env].label}
      </span>
      <ButtonLink href="/superadmin" size="xs" text icon={<Editor.Wand />} className="text-slate-200 hover:text-white">
        Superadmin
      </ButtonLink>
      <span className="h-4 w-px bg-slate-700" />

      {impersonation != null && impersonatedUserTeam != null && (
        <div className="flex min-w-0 items-center gap-2" data-testid="superadmin-bar-acting-as">
          <TeamUserAvatar userTeam={impersonatedUserTeam} className="size-5 text-3xs" shape="circle" />
          <span className="truncate">
            {onSuperadminPage ? 'Superadmin pages run as you · elsewhere acting as ' : 'Acting as '}
            <strong className="font-semibold text-white">{impersonatedUserTeam.userTeamName}</strong>
            {!onSuperadminPage && userTeam?.teamName != null && (
              <span className="text-slate-400"> · {userTeam.teamName}</span>
            )}
          </span>
          <Button size="2xs" tone="outline" color="white" onClick={() => stopImpersonation()}>
            Stop
          </Button>
        </div>
      )}
      {impersonation == null && <span className="text-slate-400">Signed in as yourself</span>}

      <div className="ml-auto flex items-center gap-2">
        <Button
          size="xs"
          tone="outline"
          color="white"
          icon={<General.SearchSm />}
          tooltip="Switch to anyone, or jump to a superadmin page"
          onClick={() => setPaletteOpen(true)}
          data-testid="superadmin-bar-switch">
          Switch to…
          <kbd className="ml-1.5 font-sans text-slate-400">{switchShortcutLabel()}</kbd>
        </Button>
      </div>
      <SuperadminSwitchPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
};
